import Box from '@/Atoms/Box';
import Card from '@/Atoms/Card';
import CCColors, {updateColorWithOpacity} from '@/Utils/CCColors';
import Spacer from '@/Atoms/Spacer';
import React, {useEffect, useState} from 'react';
import {Text, TouchableOpacity, View, TextInput} from 'react-native';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';
import useUpdateChapterSettings from '@/Hooks/CreateNewChapterApi/useUpdateChapterSettings';
import {useToast} from '@/toast/useToast';
import {styles as workbookStyles} from '../../styles';
import {
  PRACTICE_DIFFICULTIES,
  difficultyFromElo,
} from '../../constants/practiceDifficulty';

type ChapterSettingsPanelProps = {
  chapter: any;
  gameId: string;
  setLoading: (loading: boolean) => void;
  refetchStudy: () => void;
  setSelectedChapter: (chapter: any) => void;
  /**
   * The position currently on the board, and a label for it — so "start the
   * exercise here" can pin whatever node the coach has selected in the move
   * tree without this panel needing to know anything about the tree.
   */
  boardFen?: string;
  boardPositionLabel?: string;
  /** The chapter's own start position, i.e. what "no pin" resolves to. */
  chapterStartFen?: string;
};

const CHAPTER_MODES = [
  {
    key: 'analysis',
    label: 'Normal analysis',
    desc: 'Free board analysis, draw markings, try moves.',
    icon: 'magnify',
  },
  {
    key: 'lesson',
    label: 'Interactive lesson',
    desc: 'Checks moves against the line, shows hint/success popups.',
    icon: 'lightbulb-on-outline',
  },
  {
    key: 'practice',
    label: 'Practice with computer',
    desc: 'Student plays the position out against the engine.',
    icon: 'robot-outline',
  },
] as const;

const PRACTICE_GOALS = [
  {key: 'none', label: 'No explicit goal'},
  {key: 'mate', label: 'Checkmate the opponent'},
  {key: 'mateIn', label: 'Checkmate in N moves'},
  {key: 'equalize', label: 'Equalize the position (eval back to about level)'},
  {key: 'advantage', label: 'Reach a winning advantage (about +1.5 or better)'},
  {key: 'promotion', label: 'Promote a pawn'},
] as const;

// Whether the student may retune the opponent mid-exercise. Without this a
// student can drop the engine to Beginner and satisfy any goal trivially,
// which makes the recorded result meaningless. 'free' is what every chapter
// authored before this setting existed does, so it stays the null value on
// the wire - but 'floor' is what the panel recommends for new work.
const STRENGTH_POLICIES = [
  {
    key: 'floor',
    label: 'Can raise, not lower (recommended)',
    desc: 'The student may make it harder, never easier than you set.',
  },
  {
    key: 'fixed',
    label: 'Locked',
    desc: 'The student plays exactly the strength you chose.',
  },
  {
    key: 'free',
    label: 'Student chooses',
    desc: 'Any strength. Casual practice rather than assessment.',
  },
] as const;

// null = unlimited, matching the pre-existing behaviour.
const TAKEBACK_POLICIES = [
  {key: 'unlimited', label: 'Unlimited', desc: 'Take back as often as needed.'},
  {key: '3', label: 'Up to 3', desc: 'A few second chances per attempt.'},
  {key: '1', label: 'Just one', desc: 'One mistake can be undone.'},
  {key: '0', label: 'None', desc: 'One-shot — closest to test conditions.'},
] as const;

/** Wire value (null | number) -> the key of the row that should read selected. */
function takebackKeyFromValue(value: number | null): string {
  return value == null ? 'unlimited' : String(value);
}

// One selectable row inside a Card - icon,
// title (+ optional description), and a radio dot on the right. Reuses the
// same group/row/icon shapes as the Settings tab's Share & export sub-tab
// (see Study/styles.ts) so both halves of the Settings tab read as one
// design instead of two.
function SelectableRow({
  icon,
  label,
  desc,
  isSelected,
  onPress,
  isLast,
}: {
  icon: string;
  label: string;
  desc?: string;
  isSelected: boolean;
  onPress: () => void;
  isLast?: boolean;
}) {
  return (
    <>
      <TouchableOpacity style={workbookStyles.settingsRow} onPress={onPress}>
        <Box
          style={[
            workbookStyles.settingsRowIcon,
            isSelected && {
              backgroundColor: updateColorWithOpacity(
                CCColors.TextColor.Primary,
                0.14,
              ),
            },
          ]}>
          <MaterialCommunityIcons
            name={icon}
            size={15}
            color={isSelected ? CCColors.Workbook.GoldText : '#757575'}
          />
        </Box>
        <Box style={{flex: 1}}>
          <Text
            style={{
              fontSize: 12.5,
              fontWeight: '600',
              color: isSelected ? '#2C1A11' : '#757575',
            }}>
            {label}
          </Text>
          {!!desc && (
            <Text style={{fontSize: 11, color: '#757575', marginTop: 1}}>
              {desc}
            </Text>
          )}
        </Box>
        <MaterialCommunityIcons
          name={isSelected ? 'radiobox-marked' : 'radiobox-blank'}
          size={18}
          color={isSelected ? CCColors.Workbook.GoldText : '#B5A99F'}
        />
      </TouchableOpacity>
      {!isLast && <Box style={workbookStyles.settingsRowDivider} />}
    </>
  );
}

// Study's Settings tab, Chapter sub-tab - used to be a modal opened from
// the gear icon on the selected chapter's row in Chapters/index.tsx. That
// gear icon now just switches to this tab (see Study/index.tsx's
// onOpenChapterSettings), and this panel always edits whichever chapter is
// currently selected rather than a separately-tracked "editingChapter".
function ChapterSettingsPanel({
  chapter,
  setLoading,
  refetchStudy,
  setSelectedChapter,
  boardFen,
  boardPositionLabel,
  chapterStartFen,
}: ChapterSettingsPanelProps) {
  const [editName, setEditName] = useState('');
  const [editOrientation, setEditOrientation] = useState<'W' | 'B'>('W');
  const [editMode, setEditMode] = useState<'analysis' | 'lesson' | 'practice'>(
    'analysis',
  );
  const [editConcealMoves, setEditConcealMoves] = useState(false);
  // Matches Lichess's practiceGoal chapter tag (mate/mateIn/equalize/
  // advantage/promotion). 'none' means no explicit goal - practice mode
  // then falls back to "reached the end of the authored line".
  const [editPracticeGoalType, setEditPracticeGoalType] = useState<
    'none' | 'mate' | 'mateIn' | 'equalize' | 'advantage' | 'promotion'
  >('none');
  const [editPracticeGoalValue, setEditPracticeGoalValue] = useState('');
  // Opponent strength, stored as a target Elo. Null = full strength, which is
  // how every chapter saved before this field existed reads back.
  const [editPracticeElo, setEditPracticeElo] = useState<number | null>(null);
  // Both null on the wire mean "the permissive legacy behaviour" - free
  // strength and unlimited take backs - so untouched chapters keep working
  // exactly as before.
  const [editStrengthPolicy, setEditStrengthPolicy] = useState<
    'fixed' | 'floor' | 'free' | null
  >(null);
  const [editTakebacksAllowed, setEditTakebacksAllowed] = useState<
    number | null
  >(null);
  // Null on both means "the defaults every pre-existing chapter already has":
  // start at the chapter's own first position, and take the student's colour
  // from `orientation`.
  const [editPracticeStartFen, setEditPracticeStartFen] = useState<
    string | null
  >(null);
  const [editPracticePlayerColor, setEditPracticePlayerColor] = useState<
    'w' | 'b' | null
  >(null);

  const showToast = useToast();
  const {mutate: updateChapterSettings, isLoading: isUpdatingSettings} =
    useUpdateChapterSettings();

  // Re-seed only when which chapter is selected actually changes (not on
  // every refetch of the same chapter), so an in-progress edit doesn't get
  // clobbered by a background sync while this tab is open.
  useEffect(() => {
    if (!chapter) {
      return;
    }
    setEditName(chapter.name || '');
    setEditOrientation(chapter.orientation || 'W');
    // 'hide_moves' used to be a 4th, mutually-exclusive chapter_mode value.
    // It's now a separate `conceal_moves` toggle that composes with any
    // mode, so an older chapter saved as chapter_mode='hide_moves' reads
    // back as mode='analysis' with concealment already switched on.
    const legacyHideMoves = chapter.chapter_mode === 'hide_moves';
    setEditMode(
      legacyHideMoves ? 'analysis' : chapter.chapter_mode || 'analysis',
    );
    setEditConcealMoves(Boolean(chapter.conceal_moves) || legacyHideMoves);
    const goal = chapter.practice_goal;
    setEditPracticeGoalType(goal?.type || 'none');
    setEditPracticeGoalValue(goal?.value != null ? String(goal.value) : '');
    setEditPracticeElo(
      chapter.practice_difficulty != null ? chapter.practice_difficulty : null,
    );
    setEditStrengthPolicy(chapter.practice_strength_policy ?? null);
    setEditTakebacksAllowed(
      chapter.practice_takebacks_allowed != null
        ? chapter.practice_takebacks_allowed
        : null,
    );
    setEditPracticeStartFen(chapter.practice_start_fen ?? null);
    setEditPracticePlayerColor(
      chapter.practice_player_color === 'w' ||
        chapter.practice_player_color === 'b'
        ? chapter.practice_player_color
        : null,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chapter?.id]);

  // Whose move it is in the position the exercise would start from — the
  // student's colour defaults to this, since playing the side that isn't to
  // move means handing the computer a free first move.
  const effectiveStartFen =
    editPracticeStartFen || chapterStartFen || boardFen || '';
  const startSideToMove: 'w' | 'b' =
    effectiveStartFen.split(' ')[1] === 'b' ? 'b' : 'w';
  const resolvedPlayerColor: 'w' | 'b' =
    editPracticePlayerColor ?? (editOrientation === 'B' ? 'b' : 'w');
  const isPinnedToBoard =
    !!editPracticeStartFen && !!boardFen && editPracticeStartFen === boardFen;

  const handleSaveSettings = () => {
    if (!chapter) {
      return;
    }
    // The backend rejects {type:'mateIn'} without a positive integer value
    // (_validate_practice_goal in study_game.py), so catch it here rather
    // than letting the save round-trip into a 400.
    const mateInValue = parseInt(editPracticeGoalValue, 10);
    if (
      editMode === 'practice' &&
      editPracticeGoalType === 'mateIn' &&
      !(mateInValue > 0)
    ) {
      showToast('Enter the number of moves for "Checkmate in N moves".', {
        type: 'Error',
      });
      return;
    }
    setLoading(true);
    const practiceGoal =
      editMode === 'practice' && editPracticeGoalType !== 'none'
        ? {
            type: editPracticeGoalType,
            ...(editPracticeGoalType === 'mateIn' ? {value: mateInValue} : {}),
          }
        : null;
    // The policies only mean anything for an assessed Exercise, so leaving
    // practice mode clears them back to null (= the permissive default)
    // rather than leaving a stale rule on an analysis chapter.
    const strengthPolicy = editMode === 'practice' ? editStrengthPolicy : null;
    const takebacksAllowed =
      editMode === 'practice' ? editTakebacksAllowed : null;
    // Same rule as the policies above: these only mean anything for an
    // Exercise, so leaving practice mode clears them rather than stranding a
    // pinned start position on an analysis chapter.
    const practiceStartFen =
      editMode === 'practice' ? editPracticeStartFen : null;
    const practicePlayerColor =
      editMode === 'practice' ? editPracticePlayerColor : null;
    updateChapterSettings(
      {
        chapter_id: chapter.id,
        name: editName,
        orientation: editOrientation,
        chapter_mode: editMode,
        conceal_moves: editConcealMoves,
        practice_goal: practiceGoal,
        practice_difficulty: editMode === 'practice' ? editPracticeElo : null,
        practice_strength_policy: strengthPolicy,
        practice_takebacks_allowed: takebacksAllowed,
        practice_start_fen: practiceStartFen,
        practice_player_color: practicePlayerColor,
      },
      {
        onSuccess: () => {
          setLoading(false);
          // Apply the saved fields to the in-memory chapter right away, so
          // mode-dependent UI (practice/lesson/conceal) updates immediately
          // instead of waiting on refetchStudy's round trip.
          setSelectedChapter({
            ...chapter,
            name: editName,
            orientation: editOrientation,
            chapter_mode: editMode,
            conceal_moves: editConcealMoves,
            practice_goal: practiceGoal,
            practice_difficulty:
              editMode === 'practice' ? editPracticeElo : null,
            practice_strength_policy: strengthPolicy,
            practice_takebacks_allowed: takebacksAllowed,
            practice_start_fen: practiceStartFen,
            practice_player_color: practicePlayerColor,
          });
          refetchStudy();
        },
        onError: (err: any) => {
          setLoading(false);
          // axios's own err.message is just "Request failed with status code
          // 400" - the actual reason (e.g. an invalid/finished
          // practice_start_fen) is in the response body, which was being
          // swallowed here, leaving the coach with no way to tell why the
          // save failed.
          const backendMessage = err?.response?.data?.error;
          showToast(
            'Failed to update chapter settings' +
              (backendMessage
                ? `: ${backendMessage}`
                : err?.message
                ? `: ${err.message}`
                : ''),
            {type: 'Error'},
          );
        },
      },
    );
  };

  if (!chapter) {
    return (
      <Box style={{padding: 24, alignItems: 'center'}}>
        <MaterialCommunityIcons
          name="chess-rook"
          size={28}
          color={updateColorWithOpacity(CCColors.TextColor.Primary, 0.35)}
        />
        <Spacer spacing={8} />
        <Text
          style={{
            fontSize: 12,
            color: '#757575',
            textAlign: 'center',
          }}>
          Select a chapter to edit its settings.
        </Text>
      </Box>
    );
  }

  return (
    <Box style={{padding: 12, gap: 14}}>
      {/* Chapter Name */}
      <View style={{gap: 6}}>
        <Text
          style={{
            fontSize: 10.5,
            fontWeight: 'bold',
            color: '#9E9E9E',
            letterSpacing: 0.5,
          }}>
          CHAPTER NAME
        </Text>
        <TextInput
          value={editName}
          onChangeText={setEditName}
          placeholder="e.g. Chapter 1"
          placeholderTextColor="#A39081"
          style={{
            height: 34,
            borderRadius: 8,
            borderWidth: 1,
            borderColor: '#EAE2D5',
            backgroundColor: '#FFFFFF',
            paddingHorizontal: 10,
            fontSize: 12,
            color: '#1E110A',
          }}
        />
      </View>

      {/* Board Orientation */}
      <View style={{gap: 6}}>
        <Text
          style={{
            fontSize: 10.5,
            fontWeight: 'bold',
            color: '#9E9E9E',
            letterSpacing: 0.5,
          }}>
          BOARD ORIENTATION
        </Text>
        <View
          style={{
            flexDirection: 'row',
            backgroundColor: '#F5F5F5',
            borderRadius: 8,
            padding: 3,
            height: 32,
          }}>
          {(['W', 'B'] as const).map(o => (
            <TouchableOpacity
              key={o}
              onPress={() => setEditOrientation(o)}
              activeOpacity={0.8}
              style={{
                flex: 1,
                borderRadius: 6,
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'row',
                gap: 6,
                backgroundColor:
                  editOrientation === o ? '#FFFFFF' : 'transparent',
                shadowColor: editOrientation === o ? '#000' : 'transparent',
                shadowOffset: {width: 0, height: 1},
                shadowOpacity: editOrientation === o ? 0.12 : 0,
                shadowRadius: 2,
                elevation: editOrientation === o ? 1 : 0,
              }}>
              <View
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: 5,
                  backgroundColor: o === 'W' ? '#FFFFFF' : '#2C1A11',
                  borderWidth: 1,
                  borderColor: '#2C1A11',
                }}
              />
              <Text
                style={{
                  fontSize: 11.5,
                  fontWeight: editOrientation === o ? 'bold' : 'normal',
                  color: editOrientation === o ? '#1E110A' : '#757575',
                }}>
                {o === 'W' ? 'White' : 'Black'}
              </Text>
            </TouchableOpacity>
          ))}
        </View>
      </View>

      {/* Chapter Mode */}
      <Box>
        <Text
          style={{
            fontSize: 10.5,
            fontWeight: 'bold',
            color: '#9E9E9E',
            letterSpacing: 0.5,
            marginBottom: 6,
          }}>
          CHAPTER MODE
        </Text>
        <Card style={{overflow: 'hidden'}}>
          {CHAPTER_MODES.map((item, idx) => (
            <SelectableRow
              key={item.key}
              icon={item.icon}
              label={item.label}
              desc={item.desc}
              isSelected={editMode === item.key}
              onPress={() => setEditMode(item.key)}
              isLast={idx === CHAPTER_MODES.length - 1}
            />
          ))}
        </Card>
      </Box>

      {/* An author editing a practice chapter sees the ordinary analysis
          board, so there's nothing on screen telling them how the chapter
          will actually behave for a student. Point them at the preview
          toggle (the eye icon in the board toolbar), which is the existing
          way to see a chapter exactly as a participant does. */}
      {editMode === 'practice' && (
        <Box
          style={{
            flexDirection: 'row',
            gap: 8,
            padding: 10,
            borderRadius: 8,
            backgroundColor: CCColors.LightYellow,
          }}>
          <MaterialCommunityIcons
            name="eye-outline"
            size={15}
            color={CCColors.Workbook.GoldText}
          />
          <Text
            style={{flex: 1, fontSize: 11, color: '#5A4A3F', lineHeight: 16}}>
            Students play this position out against the engine — the authored
            moves are only the reference line. Use the eye icon above the board
            to preview the chapter as a participant and try it yourself.
          </Text>
        </Box>
      )}

      {/* Which position the exercise is played from. Until this existed the
          answer was always "the chapter's first position", which made a
          practice chapter useless for drilling anything that happens later in
          a line - the coach had to split the line into its own chapter just to
          move the starting point. Pinning stores a FEN rather than a move id
          so it survives the tree being re-imported or edited. */}
      {editMode === 'practice' && (
        <Box>
          <Text
            style={{
              fontSize: 10.5,
              fontWeight: 'bold',
              color: '#9E9E9E',
              letterSpacing: 0.5,
              marginBottom: 6,
            }}>
            START THE EXERCISE FROM
          </Text>
          <Card style={{overflow: 'hidden'}}>
            <SelectableRow
              icon="flag-outline"
              label="The chapter's starting position"
              desc="Where the chapter itself begins."
              isSelected={!editPracticeStartFen}
              onPress={() => setEditPracticeStartFen(null)}
            />
            <SelectableRow
              icon="target"
              label={
                editPracticeStartFen && !isPinnedToBoard
                  ? 'A pinned position'
                  : 'The position on the board now'
              }
              desc={
                editPracticeStartFen && !isPinnedToBoard
                  ? 'Select a different move and tap here to re-pin.'
                  : boardPositionLabel ||
                    'Select any move in the tree, then tap here.'
              }
              isSelected={!!editPracticeStartFen}
              onPress={() => boardFen && setEditPracticeStartFen(boardFen)}
              isLast
            />
          </Card>
          {!!editPracticeStartFen && !isPinnedToBoard && (
            <Text
              style={{fontSize: 10.5, color: '#757575', marginTop: 6}}
              numberOfLines={1}>
              Pinned: {editPracticeStartFen}
            </Text>
          )}
        </Box>
      )}

      {/* Which colour the student plays. Separate from board orientation: a
          coach may well want a black-to-play position shown from White's side,
          and before this the two were the same setting. Null keeps reading
          from `orientation` so existing chapters are untouched. */}
      {editMode === 'practice' && (
        <Box>
          <Text
            style={{
              fontSize: 10.5,
              fontWeight: 'bold',
              color: '#9E9E9E',
              letterSpacing: 0.5,
              marginBottom: 6,
            }}>
            THE STUDENT PLAYS
          </Text>
          <Card style={{overflow: 'hidden'}}>
            {[
              {
                key: 'w' as const,
                label: 'White',
                icon: 'chess-king',
              },
              {
                key: 'b' as const,
                label: 'Black',
                icon: 'chess-king',
              },
            ].map((side, idx) => (
              <SelectableRow
                key={side.key}
                icon={side.icon}
                label={side.label}
                desc={
                  side.key === startSideToMove
                    ? 'Moves first from this position.'
                    : 'The computer moves first from this position.'
                }
                isSelected={resolvedPlayerColor === side.key}
                onPress={() => setEditPracticePlayerColor(side.key)}
                isLast={idx === 1}
              />
            ))}
          </Card>
          {editPracticePlayerColor == null && (
            <Text style={{fontSize: 10.5, color: '#757575', marginTop: 6}}>
              Following the board orientation (
              {editOrientation === 'B' ? 'Black' : 'White'}). Pick a side to set
              it independently.
            </Text>
          )}
        </Box>
      )}

      {/* Opponent strength - practice only. The student can dial this down
          further for themselves from the practice panel; this is the default
          the chapter opens at. */}
      {editMode === 'practice' && (
        <Box>
          <Text
            style={{
              fontSize: 10.5,
              fontWeight: 'bold',
              color: '#9E9E9E',
              letterSpacing: 0.5,
              marginBottom: 6,
            }}>
            OPPONENT STRENGTH
          </Text>
          <Card style={{overflow: 'hidden'}}>
            {PRACTICE_DIFFICULTIES.map((level, idx) => (
              <SelectableRow
                key={level.key}
                icon="robot-outline"
                label={level.label}
                desc={level.desc}
                isSelected={
                  difficultyFromElo(editPracticeElo).key === level.key
                }
                onPress={() => setEditPracticeElo(level.elo)}
                isLast={idx === PRACTICE_DIFFICULTIES.length - 1}
              />
            ))}
          </Card>
        </Box>
      )}

      {/* Whether the student may retune that strength. Only worth showing
          once a strength is actually capped - against a full-strength
          opponent there is nothing to lower from, so a floor means nothing. */}
      {editMode === 'practice' && (
        <Box>
          <Text
            style={{
              fontSize: 10.5,
              fontWeight: 'bold',
              color: '#9E9E9E',
              letterSpacing: 0.5,
              marginBottom: 6,
            }}>
            CAN THE STUDENT CHANGE IT?
          </Text>
          <Card style={{overflow: 'hidden'}}>
            {STRENGTH_POLICIES.map((policy, idx) => (
              <SelectableRow
                key={policy.key}
                icon="lock-outline"
                label={policy.label}
                desc={policy.desc}
                // null is stored as 'free' semantically, so an untouched
                // chapter shows "Student chooses" selected rather than blank.
                isSelected={(editStrengthPolicy ?? 'free') === policy.key}
                onPress={() => setEditStrengthPolicy(policy.key)}
                isLast={idx === STRENGTH_POLICIES.length - 1}
              />
            ))}
          </Card>
        </Box>
      )}

      {/* Take backs are part of the result: a win with eleven of them is not
          the same as a clean one, so the coach gets to bound them. */}
      {editMode === 'practice' && (
        <Box>
          <Text
            style={{
              fontSize: 10.5,
              fontWeight: 'bold',
              color: '#9E9E9E',
              letterSpacing: 0.5,
              marginBottom: 6,
            }}>
            TAKE BACKS PER ATTEMPT
          </Text>
          <Card style={{overflow: 'hidden'}}>
            {TAKEBACK_POLICIES.map((policy, idx) => (
              <SelectableRow
                key={policy.key}
                icon="undo-variant"
                label={policy.label}
                desc={policy.desc}
                isSelected={
                  takebackKeyFromValue(editTakebacksAllowed) === policy.key
                }
                onPress={() =>
                  setEditTakebacksAllowed(
                    policy.key === 'unlimited' ? null : Number(policy.key),
                  )
                }
                isLast={idx === TAKEBACK_POLICIES.length - 1}
              />
            ))}
          </Card>
        </Box>
      )}

      {/* Practice Goal - only meaningful for Practice with computer; null
          ('No explicit goal') falls back to "reached the end of the
          authored line". "Equalize"/"Reach a winning advantage" are decided
          by the engine eval after the student's move — the thresholds live
          in utils/practiceOutcome.ts. */}
      {editMode === 'practice' && (
        <Box>
          <Text
            style={{
              fontSize: 10.5,
              fontWeight: 'bold',
              color: '#9E9E9E',
              letterSpacing: 0.5,
              marginBottom: 6,
            }}>
            PRACTICE GOAL (OPTIONAL)
          </Text>
          <Card style={{overflow: 'hidden'}}>
            {PRACTICE_GOALS.map((item, idx) => (
              <SelectableRow
                key={item.key}
                icon="target"
                label={item.label}
                isSelected={editPracticeGoalType === item.key}
                onPress={() => setEditPracticeGoalType(item.key)}
                isLast={idx === PRACTICE_GOALS.length - 1}
              />
            ))}
          </Card>
          {editPracticeGoalType === 'mateIn' && (
            <TextInput
              value={editPracticeGoalValue}
              onChangeText={setEditPracticeGoalValue}
              placeholder="Number of moves, e.g. 3"
              placeholderTextColor="#A39081"
              keyboardType="numeric"
              style={{
                height: 34,
                borderRadius: 8,
                borderWidth: 1,
                borderColor: '#EAE2D5',
                backgroundColor: '#FFFFFF',
                paddingHorizontal: 10,
                fontSize: 12,
                color: '#1E110A',
                marginTop: 8,
              }}
            />
          )}
        </Box>
      )}

      {/* Options - "Conceal upcoming moves" (hide moves) is hidden for now,
          still in development. */}

      {/* Save Button */}
      <TouchableOpacity
        style={{
          backgroundColor: '#FACF47',
          paddingVertical: 10,
          borderRadius: 20,
          alignItems: 'center',
          justifyContent: 'center',
          opacity: isUpdatingSettings ? 0.5 : 1,
          shadowColor: '#FACF47',
          shadowOffset: {width: 0, height: 2},
          shadowOpacity: 0.15,
          shadowRadius: 4,
          elevation: 2,
          marginTop: 6,
        }}
        onPress={handleSaveSettings}
        disabled={isUpdatingSettings}>
        <Text style={{fontSize: 12.5, color: '#1E110A', fontWeight: 'bold'}}>
          {isUpdatingSettings ? 'Saving…' : 'Save chapter settings'}
        </Text>
      </TouchableOpacity>
    </Box>
  );
}

export default ChapterSettingsPanel;
